import cn from 'classnames';
import React, { useEffect, useState } from 'react';

import IconMinus from 'editor/src/component/Icon/IconMinus';
import IconPlus from 'editor/src/component/Icon/IconPlus';

import styles from './index.module.scss';

interface Props {
  value: number;
  min: number;
  max?: number;
  onChange: (value: number) => void;
  incrementDisabled?: boolean;
  suffix?: string;
  className?: string;
}

function QuantityStepper({ value, min, max, onChange, incrementDisabled, suffix, className }: Props) {
  const [draft, setDraft] = useState(String(value));

  useEffect(() => {
    setDraft(String(value));
  }, [value]);

  const clamp = (candidate: number) => {
    let clamped = Math.max(min, Math.round(candidate));
    if (max !== undefined) {
      clamped = Math.min(max, clamped);
    }
    return clamped;
  };

  const commitDraft = () => {
    const parsed = parseInt(draft, 10);
    if (Number.isNaN(parsed)) {
      setDraft(String(value));
      return;
    }
    const clamped = clamp(parsed);
    setDraft(String(clamped));
    if (clamped !== value) {
      onChange(clamped);
    }
  };

  const decrementDisabled = value <= min;
  const plusDisabled = incrementDisabled || (max !== undefined && value >= max);

  return (
    <div className={cn(styles.QuantityStepper, className)}>
      <button
        type="button"
        // TODO: Lokalino
        aria-label="Decrease"
        className={cn(styles.stepButton, 'cy-stepper-decrement')}
        disabled={decrementDisabled}
        onClick={() => onChange(clamp(value - 1))}
      >
        <IconMinus className={styles.stepIcon} />
      </button>
      <input
        className={cn(styles.valueInput, 'cy-stepper-input')}
        type="text"
        inputMode="numeric"
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commitDraft}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            (e.target as HTMLInputElement).blur();
          }
        }}
      />
      {suffix && <div className={styles.suffix}>{suffix}</div>}
      <button
        type="button"
        // TODO: Lokalino
        aria-label="Increase"
        className={cn(styles.stepButton, 'cy-stepper-increment')}
        disabled={plusDisabled}
        onClick={() => onChange(clamp(value + 1))}
      >
        <IconPlus className={styles.stepIcon} />
      </button>
    </div>
  );
}

export default React.memo(QuantityStepper);
